import { fileTypeFromBuffer } from 'file-type';
import React, { useState, forwardRef, useEffect } from 'react';
import first from 'lodash/first';
import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import TransferSpinner from './TransferSpinner';
import TransferContext from 'src/presentation/contexts/TransferContext';
import analytics from '@ad-scripts/lib/analytics';
import useUploadRecording from 'src/presentation/components/pages/shared/hooks/useUploadRecording';
import { FileInput } from 'src/presentation/components/forms/FileInput';
import {
  TWILIO_FILE_SIZE_EXCEEDED_DEFAULT_ERROR_MESSAGE,
  ALLOWED_TWILIO_AUDIO_FILE_SIZE_B,
  BROWSER_MIME_MAP,
  SUPPORTED_MIME_TYPES,
} from 'src/presentation/components/pages/shared/constants/commonConstants';
import { getRecordingFileName } from 'src/presentation/components/pages/shared/utils/Utils';
import { toastError } from 'src/actions/toast';

const DEFAULT_ALLOWED_AUDIO_FILES = SUPPORTED_MIME_TYPES.join(',');

type AudioSaveComponentProps = {
  allRecordings?: any[];
  fileExt?: string;
  onClick?: Function;
  onSave?: Function;
  recordingUrl?: string;
  ref: HTMLInputElement | null;
  templated?: null | { templatePrefix: string; templatePostfix: string };
};

// allows selecting the same file in file input more than 1 time
// https://stackoverflow.com/questions/12030686/html-input-file-selection-event-not-firing-upon-selecting-the-same-file
const defaultOnClickFn = e => (e.target.value = null);

// for some reason browser uses audio/wav as mime for wave files.
// https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
const resolveMimeType = (mime: string) => (has(BROWSER_MIME_MAP, mime) ? BROWSER_MIME_MAP[mime] : mime);

const AudioSaver = forwardRef<HTMLInputElement, AudioSaveComponentProps>(
  ({ allRecordings = [], fileExt, onClick = defaultOnClickFn, onSave, recordingUrl, templated }, ref) => {
    const [file, setFile] = useState<any>();
    const [transferStatus, setTransferStatus] = useState<string>('');
    const [showTransferSpinner, setShowTransferSpinner] = useState(false);
    const dispatch = useDispatch();
    const fileExtensions = recordingUrl ? `.${recordingUrl.split('.').pop()}` : fileExt || DEFAULT_ALLOWED_AUDIO_FILES;
    const [fileName, setFileName] = useState<string>(getRecordingFileName(recordingUrl));
    const shouldEncodeAudio = !templated;
    const uploadRecording = useUploadRecording();

    useEffect(() => {
      setFileName(getRecordingFileName(recordingUrl));
    }, [recordingUrl]);

    const saveSelectedRecording = async fileList => {
      const selectedFile: File | undefined = first(fileList);

      if (!selectedFile) {
        return;
      }

      const fileSize = get(selectedFile, 'size');
      const selectedFileName = get(selectedFile, 'name', '');
      const fileMimeFromName = get(selectedFile, 'type', '');

      if (fileSize > ALLOWED_TWILIO_AUDIO_FILE_SIZE_B) {
        dispatch(toastError(TWILIO_FILE_SIZE_EXCEEDED_DEFAULT_ERROR_MESSAGE));
        return;
      }

      const fileType = await fileTypeFromBuffer(await selectedFile.arrayBuffer());

      if (!fileType || !SUPPORTED_MIME_TYPES.includes(fileType.mime)) {
        dispatch(toastError('This file type is not supported. Please convert to MP3 or WAV.'));
        return;
      }

      if (fileType.mime !== resolveMimeType(fileMimeFromName)) {
        dispatch(
          toastError(
            `File type "${fileMimeFromName}" does not match file format "${fileType.mime}". Please use a file converter to convert to MP3 or WAV.`
          )
        );
        return;
      }

      // providing `recordingUrl` in templated mode is for replacing audio
      // in this case the `templated` restrictions must be omitted
      if (templated && !recordingUrl) {
        if (
          !selectedFileName.startsWith(templated.templatePrefix) ||
          !selectedFileName.endsWith(templated.templatePostfix)
        ) {
          dispatch(
            toastError(
              `You can only upload recordings named ${templated.templatePrefix}...${templated.templatePostfix}`
            )
          );
          return;
        }
      }

      const newFileName =
        shouldEncodeAudio && fileType.ext !== 'mp3' ? getEncodedFilename(selectedFileName) : selectedFileName;

      // `id` in `allRecordings` stands for `name`
      if (!recordingUrl && map(allRecordings, 'id').includes(newFileName)) {
        dispatch(toastError(`${newFileName} already exists.`));
        return;
      }

      // updates the spinner file info with the new recording's name
      if (!recordingUrl) {
        setFileName(newFileName);
      }

      setShowTransferSpinner(true);
      setTransferStatus('Uploading & Converting recording to');
      const assetUrl = await uploadRecording(selectedFileName, selectedFile);
      const result = assetUrl?.url;

      setShowTransferSpinner(false);
      onSave && onSave(result);

      analytics.track(`Admin - Recording Library - ${recordingUrl ? 'Uploaded New' : 'Replaced'} File`, {
        fileName: newFileName,
      });
    };

    const onFileChange = async fileList => {
      if (isEmpty(fileList)) {
        return;
      }

      await saveSelectedRecording(fileList);
      setFile('');
    };

    return (
      <>
        {showTransferSpinner && (
          <TransferContext.Provider value={{ status: transferStatus }}>
            <TransferSpinner filename={fileName} isReplacing={!!recordingUrl} />
          </TransferContext.Provider>
        )}
        <StyledFileInput
          ref={ref}
          onChange={onFileChange}
          allowedFileTypes={fileExtensions}
          onClick={onClick}
          value={file}
        />
      </>
    );
  }
);

const getEncodedFilename = (filename: string) => {
  const parts = filename.split('.');
  parts.pop(); // remove ext
  parts.push('mp3');
  return parts.join('.');
};

const StyledFileInput = styled(FileInput)`
  display: none;
`;

export default AudioSaver;
